import { modalContentsDefaultsState } from 'Components/ModalContentDefaults/recoil/state'
import {
  LOGIN_TYPE_WEBLOGIN,
  MESSAGE_STR_TYPE_CONFIRMATION,
  MESSAGE_STR_TYPE_GENERIC,
  MSG_PLACEHOLDERS_SPECIAL_LINKS,
  STORE_KEY_LOGIN_TYPE
} from 'Constants/index'
import getIntl from 'Helpers/intl'
import { storeGet } from 'Helpers/localStorage'
import { logAppError } from 'Helpers/logger'
import {
  addTemplate,
  checkTemplatesLimitsExceeded,
  copyTemplate,
  createMessage,
  deleteAllConcurrentAccess,
  deleteAutosave,
  deleteConcurrentAccess,
  generatePreview,
  getBeeMessageToken,
  getBeePluginConfig,
  getBeeTemplateDetails,
  getConcurrentAccessUser,
  getDynamicFieldsWhitelist,
  getFilters,
  getGroups,
  getListActivation,
  getMessageActivation,
  getMessageAutoSave,
  getMessageDetails,
  getMessageDynamicContents,
  getPreviewReferenceRecipient,
  getRequiresUpdateWelcomeSeries,
  getTargetLists,
  getThirdPartyAccepted,
  getUsedTokens,
  insertConcurrentAccess,
  searchRecipients,
  setDynamicFieldsWhitelist,
  setThirdPartyResourceAlert,
  storeUsedTokens,
  updateAutosave,
  updateBeeMessage,
  updateConcurrentAccess,
  updatePreviewReferenceRecipient,
  updateWelcomeNotification,
  validateMessageForNotification
} from 'Pages/MessageEdit/recoil/actions/actions'
import {
  beePluginState,
  editorState,
  openAIState
} from 'Pages/MessageEdit/recoil/state/editor'
import {
  modalConcurrentAccess,
  modalConfirmEnableNotificationState,
  modalConfirmExitState,
  modalConfirmLeaveState,
  modalErrorState,
  modalOfferLightUpsellState,
  modalPreview,
  modalResumeAutoSave,
  modalSave,
  modalSaveAsTemplateState,
  modalThirdPartyState
} from 'Pages/MessageEdit/recoil/state/modals'
import { pageErrorState, pageState } from 'Pages/MessageEdit/recoil/state/page'
import { STORE_KEY_ID_LIST } from 'mailup-react-helpers'

import { modalAIAssistant } from 'Components/ModalAiAssistantBee/recoil/state/modal'
import { toastState } from '../../../../recoil'
import {
  filtersState,
  groupsState,
  previewLastSelected,
  previewState
} from '../state/preview'

export const MATCH_FILTER_CONDITION_ALL = 'All'
export const EMAIL_OPTOUT_TYPE_ALL = -1
export const SMS_OPTOUT_TYPE_ALL = -1

export const initLoadPluginCallback = ({ set }) => async ({
  id,
  mode,
  template,
  idTemplate,
  isNewNotificationMessage,
  enableNotification
}) => {
  set(editorState, prev => ({
    ...prev,
    loading: true,
    id,
    mode,
    template,
    idTemplate,
    isNewNotificationMessage,
    enableNotification
  }))

  try {
    const thirdPartyAccepted = await getThirdPartyAccepted()
    const concurrentAccessUsername = await getConcurrentAccessUser(id)
    const { IdAutosaveData: idAutosave, Structure: autosaveStructure } =
      (await getMessageAutoSave(id)) || {}

    const {
      MessageType: messageType,
      Structure: structure,
      Subject: subject,
      Content
    } = await (id
      ? getMessageDetails(id)
      : { messageType: MESSAGE_STR_TYPE_GENERIC, subject: '' })

    const { Structure: templateStructure, Title } = await (idTemplate
      ? getBeeTemplateDetails(idTemplate, template)
      : {})

    const requiresUpdateWelcomeSeries = await getRequiresUpdateWelcomeSeries(
      messageType
    )

    set(editorState, prev => ({
      ...prev,
      thirdPartyAccepted,
      concurrentAccessUsername,
      //
      idAutosave,
      autosaveStructure,
      templateStructure,
      structure,
      //
      subject: subject,
      Title,
      //
      messageType,
      requiresUpdateWelcomeSeries,
      //
      lastContent: Content
    }))
    set(pageState, prev => ({
      ...prev,
      loading: false,
      title: id
        ? subject
        : template === 'local'
        ? Title
        : getIntl().formatMessage({ id: 'Load.Common.WithoutName' })
    }))

    checkLoadPlugin(
      set,
      thirdPartyAccepted,
      concurrentAccessUsername,
      idAutosave
    )
  } catch (error) {
    logAppError({
      appError: error
    })
    console.error(error)
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }

  set(editorState, prev => ({
    ...prev,
    loading: false
  }))
}

const checkLoadPlugin = (
  set,
  thirdPartyAccepted,
  concurrentAccessUsername,
  idAutosave
) => {
  //

  if (!thirdPartyAccepted) {
    set(modalThirdPartyState, prev => ({
      ...prev,
      show: true
    }))
    return false
  }
  if (concurrentAccessUsername) {
    set(modalConcurrentAccess, prev => ({
      ...prev,
      show: true
    }))
    return false
  }

  if (idAutosave) {
    set(modalResumeAutoSave, prev => ({
      ...prev,
      show: true
    }))
    return false
  }
  set(editorState, prev => ({
    ...prev,
    loadPluginChecked: true
  }))
  return true
}

export const updateThirdPartyCallback = ({
  set,
  snapshot,
  reset
}) => async () => {
  set(editorState, prev => ({
    ...prev,
    loading: true
  }))

  try {
    const loginType = storeGet(STORE_KEY_LOGIN_TYPE)
    if (loginType === LOGIN_TYPE_WEBLOGIN) {
      await setThirdPartyResourceAlert()
    }
    set(editorState, prev => ({
      ...prev,
      thirdPartyAccepted: true
    }))

    reset(modalThirdPartyState)
  } catch (error) {
    logAppError({
      appError: error
    })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }
  set(editorState, prev => ({
    ...prev,
    loading: false
  }))

  const { idAutosave, concurrentAccessUsername } = await snapshot.getPromise(
    editorState
  )
  checkLoadPlugin(set, true, concurrentAccessUsername, idAutosave)
}

export const modalSaveCallback = ({ set }) => async () => {
  set(pageState, prev => ({
    ...prev,
    loading: true
  }))

  try {
    const hasExceededTemplateLimits = await checkTemplatesLimitsExceeded()

    if (hasExceededTemplateLimits) {
      set(modalOfferLightUpsellState, { show: true })
    } else {
      const idList = storeGet(STORE_KEY_ID_LIST)
      const lists = await getTargetLists([idList])

      set(modalSaveAsTemplateState, prev => ({
        ...prev,
        show: true,
        saveToLists: [idList],
        lists
      }))
    }
  } catch (error) {
    logAppError({ appError: error })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }

  set(pageState, prev => ({
    ...prev,
    loading: false
  }))
}

export const discardAutosaveCallback = ({ set, reset }) => async () => {
  reset(modalResumeAutoSave)
  reset(modalPreview)
  set(editorState, prev => ({
    ...prev,
    resume: false
  }))
  checkLoadPlugin(set, true, undefined, undefined)
}

export const resumeAutosaveCallback = ({ set, reset }) => async () => {
  reset(modalResumeAutoSave)
  reset(modalPreview)
  set(editorState, prev => ({
    ...prev,
    resume: true
  }))
  checkLoadPlugin(set, true, undefined, undefined)
}

export const openPreviewCallback = ({ set, reset }) => async () => {
  reset(modalResumeAutoSave)
  set(modalPreview, prev => ({
    ...prev,
    show: true
  }))
}

export const initBeePluginMessageCallback = ({ set, snapshot }) => async ({
  settings
}) => {
  const {
    resume,
    id,
    autosaveStructure,
    structure,
    templateStructure,
    concurrentAccessUsername
  } = await snapshot.getPromise(editorState)
  set(pageState, prev => ({
    ...prev,
    loading: true
  }))

  // const onLoad = () => {
  //   // set(beePluginState, prev => ({ ...prev, loaded: true }))
  // }

  const onError = err => {
    logAppError({
      appError: err,
      Component: 'MessageEdit',
      Method: 'BeePluginEditor',
      Message: 'Error triggered by Bee Plugin'
    })
  }

  try {
    const token = await getBeeMessageToken()
    const config = await getBeePluginConfig()
    const dynamicFieldsWhitelist = await getDynamicFieldsWhitelist('NL', id)
    //
    if (concurrentAccessUsername) {
      await updateConcurrentAccess(id)
    } else {
      await deleteAllConcurrentAccess(id)
      await insertConcurrentAccess(id)
    }

    const structureString = resume
      ? autosaveStructure
      : id
      ? structure
      : templateStructure

    set(editorState, prev => ({
      ...prev,
      mergeTags: [
        ...config.mergeTags.map(({ name }) => name),
        ...MSG_PLACEHOLDERS_SPECIAL_LINKS
      ],
      mergeTagsWhitelist: dynamicFieldsWhitelist
    }))

    const beeConfig = {
      ...config,
      // onLoad,
      onError,
      ...settings
    }

    window.BeePlugin.create(token, beeConfig, instance => {
      set(beePluginState, prev => ({ ...prev, instance }))
      instance.start(JSON.parse(structureString))
    })
  } catch (error) {
    console.error(error)
    logAppError({
      appError: error
    })
    set(pageErrorState, {
      error: getIntl().formatMessage({
        id: 'Load.Bee.EditorInitializationError'
      }),
      severity: 'danger'
    })
  }
  set(pageState, prev => ({
    ...prev,
    loading: false
  }))
}

export const saveMessageAsTemplateCallback = ({
  set,
  snapshot,
  reset
}) => async (json, cb) => {
  const { title, lists, selectAll, saveToLists } = await snapshot.getPromise(
    modalSaveAsTemplateState
  )

  set(pageState, prev => ({
    ...prev,
    loading: true
  }))

  try {
    const idCurrentList = storeGet(STORE_KEY_ID_LIST)

    const filteredList = selectAll
      ? lists
          .filter(({ value }) => idCurrentList !== value)
          .map(({ value }) => value)
      : saveToLists.filter(idList => idCurrentList !== idList)

    const { Id: newId } = await addTemplate(idCurrentList, {
      Structure: json,
      Title: title
    })

    if (filteredList.length > 0) {
      await copyTemplate(newId, title, filteredList)
    }

    reset(modalSaveAsTemplateState)

    if (cb && typeof cb === 'function') {
      cb()
    }

    set(toastState, {
      message: getIntl().formatMessage({ id: 'Load.Bee.TemplateSaved' })
    })
  } catch (error) {
    logAppError({ appError: error })
    set(modalErrorState, {
      show: true
    })
  }

  set(pageState, prev => ({
    ...prev,
    loading: false
  }))
}

export const addMergeTagToWhitelistCallback = ({
  set,
  snapshot
}) => async tag => {
  const { mergeTagsWhitelist, id } = await snapshot.getPromise(editorState)
  const newMergeTagsWhitelist = new Set(mergeTagsWhitelist).add(tag)

  try {
    setDynamicFieldsWhitelist('NL', id, [...newMergeTagsWhitelist])
    set(editorState, prev => ({
      ...prev,
      mergeTagsWhitelist: [...newMergeTagsWhitelist]
    }))
  } catch (error) {
    logAppError({ appError: error })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }
}

export const updateAutosaveCallback = ({ snapshot }) => async json => {
  const { id, subject, lastContent } = await snapshot.getPromise(editorState)

  try {
    await updateAutosave(id, {
      content: lastContent,
      subject,
      structure: json
    })

    await updateConcurrentAccess(id)
  } catch (error) {
    logAppError({ appError: error })
  }
}

export const deleteAutosaveCallback = ({ snapshot }) => async cb => {
  const { id } = await snapshot.getPromise(editorState)

  try {
    await deleteAutosave(id)
    if (cb && typeof cb === 'function') {
      cb()
    }
  } catch (error) {
    logAppError({ appError: error })
  }
}

export const enableNotificationCallback = ({
  set,
  snapshot,
  reset
}) => async doEnable => {
  const {
    id,
    messageType,
    requiresUpdateWelcomeSeries,
    afterSaveCallback
  } = await snapshot.getPromise(editorState)

  try {
    if (doEnable) {
      updateWelcomeNotification(id, messageType, requiresUpdateWelcomeSeries)
    }

    reset(modalConfirmEnableNotificationState)

    set(pageState, prev => ({
      ...prev,
      loading: false
    }))

    set(toastState, {
      message: getIntl().formatMessage({
        id: 'Load.Alerts.SuccessUpdate'
      }),
      key: 'EmailSaveSuccess'
    })

    if (typeof afterSaveCallback === 'function') {
      afterSaveCallback({ id, messageType, autoSave: 0 })
    }
  } catch (error) {
    logAppError({ appError: error })
    set(modalErrorState, {
      show: true
    })
  }
}

/**
 *
 * @param {string} variant undefined, preview, quickSend - additional message to be displayed
 */
export const openModalSaveCallback = ({ set }) => async variant => {
  set(modalSave, prev => ({
    ...prev,
    variant,
    show: true
  }))
}

export const exitCallback = ({ set, snapshot, reset }) => async (
  check = false,
  onConfirmRedirect,
  comesFromModalExit = false
) => {
  const { messageType, id, dirty, saved } = await snapshot.getPromise(
    editorState
  )

  if (comesFromModalExit) {
    set(modalConfirmLeaveState, { afterExit: true })
  }

  if (check && dirty && !saved) {
    set(modalConfirmExitState, { show: true })
  } else {
    await deleteConcurrentAccess(id)
    await deleteAutosave(id)

    reset(modalConfirmExitState)
    onConfirmRedirect(messageType)
  }
}

export const saveAndExitCallback = ({ set, snapshot }) => async ({
  onSuccess
}) => {
  const { messageType, id } = await snapshot.getPromise(editorState)

  await deleteConcurrentAccess(id)
  await deleteAutosave(id)

  typeof onSuccess === 'function' && onSuccess(id, messageType)
}

/*****************************************************************************/

export const onBeeSaveCallback = ({ set, reset, snapshot }) => async (
  jsonFile,
  htmlFile
) => {
  const {
    afterSaveCallback,
    mode,
    id,
    subject,
    messageType,
    action,
    isNewNotificationMessage,
    enableNotification,
    requiresUpdateWelcomeSeries
  } = await snapshot.getPromise(editorState)

  set(editorState, prev => ({ ...prev, lastContent: htmlFile }))
  let idNewMsg = null

  try {
    if (action === 'PREVIEW' && mode === 'edit') {
      //  preview of an existing message
      await updateAutosave(id, {
        content: htmlFile,
        structure: jsonFile,
        subject: subject
      })

      if (typeof afterSaveCallback === 'function') {
        afterSaveCallback({
          id,
          messageType,
          autoSave: 1
        })
      }
    } else if (mode !== 'edit') {
      //  create message
      await deleteAutosave(0)

      const { IdMessage } = await createMessage(subject, jsonFile, htmlFile)
      idNewMsg = IdMessage

      set(editorState, prev => ({
        ...prev,
        mode: 'edit',
        id: IdMessage,
        saved: true
      }))

      set(pageState, prev => ({ ...prev, title: subject }))

      set(toastState, {
        message: getIntl().formatMessage({ id: 'Load.Alerts.SuccessUpdate' }),
        key: 'EmailSaveSuccess'
      })

      if (typeof afterSaveCallback === 'function') {
        afterSaveCallback({
          id: IdMessage,
          messageType,
          autoSave: 0
        })
      }
    } else {
      //  update message
      const checkResult = await validateMessageForNotification(id, htmlFile)
      if (!checkResult) {
        set(modalErrorState, prev => ({
          ...prev,
          show: true,
          customErrorTitle:
            messageType === MESSAGE_STR_TYPE_CONFIRMATION
              ? getIntl().formatMessage({
                  id: 'Load.EmailMessage.ModalConfirmRequestErrorTitle'
                })
              : undefined,
          customErrorDesc:
            messageType === MESSAGE_STR_TYPE_CONFIRMATION
              ? getIntl().formatMessage(
                  {
                    id: 'Load.EmailMessage.ModalConfirmRequestErrorParagraph'
                  },
                  {
                    strong: chunks => <strong>{chunks}</strong>,
                    subscribelink: 'http://[subscribelink]/'
                  }
                )
              : undefined
        }))
      } else {
        await updateBeeMessage(id, {
          Content: htmlFile,
          Structure: jsonFile
        })

        set(editorState, prev => ({
          ...prev,
          saved: true
        }))

        await deleteAutosave(id)

        if (isNewNotificationMessage) {
          if (enableNotification) {
            updateWelcomeNotification(
              id,
              messageType,
              requiresUpdateWelcomeSeries
            )

            set(toastState, {
              message: getIntl().formatMessage({
                id: 'Load.Alerts.SuccessUpdate'
              }),
              key: 'EmailSaveSuccess'
            })

            if (typeof afterSaveCallback === 'function') {
              afterSaveCallback({
                id,
                messageType,
                autoSave: 0
              })
            }
          } else {
            set(modalConfirmEnableNotificationState, prev => ({
              ...prev,
              show: true,
              requiresUpdateWelcomeSeries,
              messageType
            }))
          }
        } else {
          set(toastState, {
            message: getIntl().formatMessage({
              id: 'Load.Alerts.SuccessUpdate'
            }),
            key: 'EmailSaveSuccess'
          })

          if (typeof afterSaveCallback === 'function') {
            afterSaveCallback({ id, messageType, autoSave: 0 })
          }
        }
      }
    }
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'MessageEdit',
      Method: 'onBeeSaveCallback'
    })

    set(editorState, prev => ({
      ...prev,
      saved: false
    }))

    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }
}

export const showPreviewCallback = ({ set, reset, snapshot }) => async ({
  id,
  autoSave
}) => {
  set(previewState, prev => ({
    ...prev,
    active: true,
    loading: true,
    autoSave
  }))

  try {
    const messageDynamicContents = await getMessageDynamicContents(id)

    const listActivation = await getListActivation()
    if (listActivation.Items && listActivation.Items.length > 0) {
      await initSelectRecipient(set)
    } else {
      const messageActivation = await getMessageActivation(id)
      if (messageActivation.Items && messageActivation.Items.length > 0) {
        await initSelectRecipient(set)
      } else {
        const { Content } = await generatePreview(id, {
          IdRecipient: 0,
          Autosave: !!autoSave,
          DynamicContentIdList: []
        })

        set(previewState, prev => ({
          ...prev,
          active: true,
          loading: false,
          html: Content,
          dynamicContents: messageDynamicContents
        }))
      }
    }

    set(previewState, prev => ({
      ...prev,
      active: true,
      loading: false,
      dynamicContents: messageDynamicContents
    }))
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'MessageEdit',
      Method: 'showPreviewCallback'
    })

    set(previewState, prev => ({
      active: true,
      loading: false,
      error: true
    }))
  }
}

async function initSelectRecipient(set) {
  set(previewState, prev => ({
    ...prev,
    hasPersonalization: true,
    showPersonalization: true
  }))

  const groups = await getGroups()
  set(groupsState, groups)

  const filters = await getFilters()
  set(filtersState, filters)

  const idList = storeGet(STORE_KEY_ID_LIST)
  const previewReferenceRecipient = await getPreviewReferenceRecipient(idList)

  let lastFilters = []
  let lastGroups = []
  let lastEmails = []

  if (previewReferenceRecipient) {
    lastGroups = buildLastElementList(
      previewReferenceRecipient.lastGroups,
      [
        ...groups,
        {
          label: getIntl().formatMessage({
            id: 'Load.SearchRecipients.nessunGruppo'
          }),
          value: 0
        }
      ],
      previewReferenceRecipient.lastTypeSelected === 'group'
    )
    lastFilters = buildLastElementList(
      previewReferenceRecipient.lastFilters,
      filters.options,
      previewReferenceRecipient.lastTypeSelected === 'filter'
    )
    lastEmails = buildLastElementList(
      previewReferenceRecipient.lastEmails,
      undefined,
      previewReferenceRecipient.lastTypeSelected === 'email'
    )

    set(previewLastSelected, prev => ({
      ...prev,
      lastGroups,
      lastFilters,
      lastEmails,
      previewReferenceRecipient
    }))
  }
}

const buildLastElementList = (list, fullList, selected) => {
  try {
    const result = []
    list.map((idElement, index) => {
      if (idElement != null) {
        let labels = []
        if (fullList) {
          labels = fullList.filter(
            listElement => listElement.value === idElement
          )
        }

        result.push({
          id: idElement,
          value: labels.length > 0 ? labels[0].label : idElement,
          selected: selected && index === 0
        })
      }
    })
    return result
  } catch (error) {
    logAppError({
      appError: error
    })
  }
  return []
}

export const updatePreviewCallback = ({
  set,
  snapshot
}) => async withDynamicContents => {
  const { id } = await snapshot.getPromise(editorState)
  const { IdRecipient, autoSave } = await snapshot.getPromise(previewState)

  set(previewState, prev => ({
    ...prev,
    active: true,
    loading: true
  }))

  try {
    const { Content } = await generatePreview(id, {
      IdRecipient: IdRecipient,
      Autosave: !!autoSave,
      DynamicContentIdList: withDynamicContents
    })

    set(previewState, prev => ({
      ...prev,
      active: true,
      loading: false,
      selectedDynamicContents: withDynamicContents,
      html: Content
    }))
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'MessageEdit',
      Method: 'updatePreviewCallback'
    })

    set(previewState, prev => ({
      ...prev,
      active: true,
      loading: false,
      error: true
    }))
  }
}

export const searchRecipientsCallback = ({ set, snapshot }) => async (
  search,
  preferenceRecipient,
  dynamicFields
) => {
  const { previewReferenceRecipient } = await snapshot.getPromise(
    previewLastSelected
  )
  set(previewState, prev => ({
    ...prev,
    loading: true
  }))

  const defaultSearchState = {
    channel: undefined,
    status: undefined,
    email: '',
    phonePrefix: '',
    phoneNumber: '',
    includeGroups: [],
    excludeGroups: [],
    applyFilters: [],
    applyFiltersOr: MATCH_FILTER_CONDITION_ALL,
    matchFieldsMap: new Map(),
    matchFields: [],
    matchFieldsValues: [],
    subscribedFrom: null,
    subscribedTo: null,
    reqSubscribedFrom: null,
    reqSubscribedTo: null,
    unsubscribedFrom: null,
    unsubscribedTo: null,
    optOutTypeEmail: EMAIL_OPTOUT_TYPE_ALL,
    optOutTypeSms: SMS_OPTOUT_TYPE_ALL,
    optOutMessage: 0,
    orderBy: 'Email',
    orderDescending: true,
    advancedSearchExpanded: false,
    pageSize: 50
  }

  try {
    const results = await searchRecipients(
      { ...defaultSearchState, ...search },
      dynamicFields
    )

    const loginType = storeGet(STORE_KEY_LOGIN_TYPE)
    if (loginType === LOGIN_TYPE_WEBLOGIN && preferenceRecipient) {
      const updateUserResult = await updatePreviewReferenceRecipient({
        ...previewReferenceRecipient,
        ...preferenceRecipient
      })

      set(previewLastSelected, prev => ({
        ...prev,
        previewReferenceRecipient: JSON.parse(updateUserResult.DataValue)
      }))
    }

    if (results.TotalItems > 0) {
      const max = results.TotalItems < 50 ? results.TotalItems - 1 : 49
      const randomIndex = Math.floor(Math.random() * (max + 1))
      const idUser = results.Items[randomIndex].id
      const recipient = results.Items[randomIndex].fields.get('Email')
      await updatePreviewFromSelectedRecipient(set, snapshot, idUser, recipient)
    }

    set(previewState, prev => ({
      ...prev,
      loading: false
    }))

    return results
  } catch (error) {
    logAppError({
      appError: error
    })

    set(previewState, prev => ({
      ...prev,
      loading: false
    }))
  }
}

async function updatePreviewFromSelectedRecipient(
  set,
  snapshot,
  IdRecipient,
  recipient
) {
  const { id } = await snapshot.getPromise(editorState)
  const { selectedDynamicContents, autoSave } = await snapshot.getPromise(
    previewState
  )

  set(previewState, prev => ({
    ...prev,
    active: true,
    loading: true
  }))

  try {
    const { Content } = await generatePreview(id, {
      IdRecipient: IdRecipient,
      Autosave: !!autoSave,
      DynamicContentIdList: selectedDynamicContents
    })

    set(previewState, prev => ({
      ...prev,
      active: true,
      loading: false,
      showPersonalization: false,
      html: Content,
      IdRecipient,
      recipient
    }))
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'MessageEdit',
      Method: 'updatePreviewCallback'
    })

    set(previewState, prev => ({
      ...prev,
      active: true,
      loading: false,
      error: true
    }))
  }
}

export const cleanupPageCallback = ({ reset }) => () => {
  reset(pageState)
  reset(pageErrorState)
  reset(modalContentsDefaultsState)
  reset(modalAIAssistant)
}

export const storeUsedTokensCounterCallback = () => async value => {
  try {
    await storeUsedTokens(value)
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'MessageEdit',
      Method: 'storeUsedTokensCounterCallback'
    })
  }
}

export const getTotalTokensCountCallback = ({ set }) => async () => {
  try {
    const resp = await getUsedTokens()
    set(openAIState, prev => ({
      ...prev,
      totalTokens: resp.TotalTokens,
      remainingTokens: resp.RemainingTokens,
      enableUse: resp.EnableUse,
      showBuy: resp.ShowBuy,
      loaded: true
    }))
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'MessageEdit',
      Method: 'getTotalTokensCountCallback'
    })
  }
}

/*

    */
